import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { addFolder, deleteFolder, fetchFolderTree, updateFolderData, fetchImageData } from '../../Slices/imageGallery/folder.slice';
import { deleteImage, fetchFolderData, uploadImage } from '../../Slices/imageGallery/uploadImage.slice';
import { FaFolder, FaFolderPlus } from "react-icons/fa";
import { MdFolderDelete } from "react-icons/md";
import { FaImage } from "react-icons/fa6";
import { ImageUploadForm } from './uploadImages';
import { SlSizeFullscreen } from "react-icons/sl";
import { FaEdit } from "react-icons/fa";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";




const FolderManager = ({ imageID }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { folderName: urlFolderName } = useParams();

  // Folder state from Redux
  const { folderTree, loading, error } = useSelector((state) => state.folders);
  const { imageDetails } = useSelector((state) => state.uploadImage);
  const [folderName, setFolderName] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [contextMenu, setContextMenu] = useState({ x: 0, y: 0, folder: null });
  const [openedFolder, setOpenedFolder] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [key, setKey] = useState(0);


  // Images States
  const [isEditMode, setIsEditMode] = useState(false);
  const [imageUpload, setImageUpload] = useState(false);
  const [imageContextMenu, setImageContextMenu] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const openedFolderId = openedFolder ? openedFolder._id : null;
  const openFolderImages = openedFolder?.images || [];

  // Function to toggle full-screen mode
  const toggleFullScreen = () => {
    setIsFullScreen((prev) => !prev);
  };

  // Fetch folder tree on component mount
  useEffect(() => {
    dispatch(fetchFolderTree())
      .then(() => console.log("Folder Tree Fetched:", folderTree))
      .catch((err) => console.error("Error fetching folder tree:", err));
  }, [dispatch]);

  useEffect(() => {
    if (openedFolderId) {
      dispatch(fetchFolderData(openedFolderId))
        .unwrap()
        .then((data) => {
          if (data && Array.isArray(data.images)) {
            console.log("✅ Images received from API:", data.images);
          } else {
            console.warn("⚠️ No images found for this folder.");
          }
        })
        .catch((err) => console.error("Error fetching folder images:", err));
    }
  }, [openedFolderId, dispatch]);




  useEffect(() => {
    setKey((prev) => prev + 1); // Increment key whenever folderTree updates
  }, [folderTree]);

  const handleImageUpload = async (formData) => {
    try {
      await dispatch(uploadImage({ formData, folderID: openedFolderId })).unwrap();
      const updatedFolderData = await dispatch(fetchFolderData(openedFolderId)).unwrap();
      updatedFolderData.images.forEach((img) => {
        if (img.fileName.endsWith(".heic")) {
          img.originalFilePath = img.originalFilePath.replace(".heic", ".jpg");
        }
      });

    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image.");
    }
  };




  const handleFolderNameChange = (e) => {
    const value = e.target.value;
    setFolderName(value);
  };

  const handleAddFolder = async () => {
    if (!folderName.trim()) {
      return alert("Folder name cannot be empty!");
    }

    const parentFolderId = openedFolder ? openedFolder._id : null;

    try {
      await dispatch(addFolder({ name: folderName.trim(), parentFolderId })).unwrap();

      setFolderName("");

      // ✅ Ensure real-time update by refetching latest folder data
      dispatch(fetchFolderTree()).then((result) => {
        if (result.payload) {
          setOpenedFolder(result.payload.find(folder => folder._id === parentFolderId));
        }
      });
    } catch (error) {
      console.error("Error Adding Folder:", error);
    }
  };





  const handleDeleteFolder = async (folderId) => {
    if (!folderId || !window.confirm('Are you sure you want to delete this folder?')) return;

    try {
      await dispatch(deleteFolder(folderId)).unwrap();

      setContextMenu({ x: 0, y: 0, folder: null });

      if (openedFolder && openedFolder._id === folderId) {
        setOpenedFolder(null);
      }

      // ✅ Ensure real-time UI update by fetching updated folder tree
      dispatch(fetchFolderTree()).then((result) => {
        if (result.payload) {
          setOpenedFolder(null); // Reset opened folder if it's deleted
        }
      });

      window.alert("Folder Deleted Successfully!");
    } catch (error) {
      console.error('Error deleting folder:', error);
      window.alert("Failed to delete folder.");
    }
  };





  // Handle click outside context menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenu.folder && !e.target.closest('.context-menu')) {
        setContextMenu({ x: 0, y: 0, folder: null });
      }
      if (imageContextMenu?.image && !e.target.closest('.context-menu')) {
        setImageContextMenu(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu, imageContextMenu]);



  useEffect(() => {
    let storedFolderId = localStorage.getItem("openedFolderId");

    if (!openedFolder && storedFolderId && folderTree.length > 0) {
      const findFolderRecursively = (folders, folderId) => {
        for (let folder of folders) {
          if (folder._id === folderId) return folder;
          if (folder.children?.length) {
            let nestedFolder = findFolderRecursively(folder.children, folderId);
            if (nestedFolder) return nestedFolder;
          }
        }
        return null;
      };

      const foundFolder = findFolderRecursively(folderTree, storedFolderId);

      if (foundFolder) {
        setOpenedFolder(foundFolder);

        // Rebuild Breadcrumb Path
        let breadcrumbPath = [];
        const findBreadcrumbPath = (folders, folderId, path = []) => {
          for (let folder of folders) {
            const newPath = [...path, folder];
            if (folder._id === folderId) {
              breadcrumbPath = newPath;
              return;
            }
            if (folder.children?.length) {
              findBreadcrumbPath(folder.children, folderId, newPath);
            }
          }
        };

        findBreadcrumbPath(folderTree, storedFolderId);
        setBreadcrumb(breadcrumbPath);
      }
    }
  }, [folderTree, openedFolder]);





  // Folder actions
  const handleFolderClick = (folder) => {
    setSelectedFolder((prev) => (prev?._id === folder._id ? null : folder));
  };

  const handleRightClickFolder = (e, folder) => {
    e.preventDefault();
    setSelectedFolder(folder);
    setContextMenu({ x: e.clientX, y: e.clientY, folder });
  };

  const handleRightClickImage = (e, image) => {
    e.preventDefault();
    setImageContextMenu({ x: e.clientX, y: e.clientY, image });
    console.log("rightClick tested:", image);

    setSelectedImage(image._id);
  };


  const handleSuperParentClick = (folder) => {
    if (!folder.parentFolderId) {
      setOpenedFolder(folder);
      setBreadcrumb([folder]);
    }
  };

  const handleDoubleClick = (folder) => {
    setOpenedFolder(folder);

    // Find Full Path for Breadcrumb
    let breadcrumbPath = [];
    const findBreadcrumbPath = (folders, targetFolder, path = []) => {
      for (let f of folders) {
        const newPath = [...path, f];
        if (f._id === targetFolder._id) {
          breadcrumbPath = newPath;
          return;
        }
        if (f.children?.length) {
          findBreadcrumbPath(f.children, targetFolder, newPath);
        }
      }
    };

    findBreadcrumbPath(folderTree, folder);
    setBreadcrumb(breadcrumbPath);

    // Store in localStorage
    localStorage.setItem("openedFolderId", folder._id);
    localStorage.setItem("openedFolderPath", JSON.stringify(breadcrumbPath.map(f => f.name)));

    // Update URL
    const newPath = breadcrumbPath.map(f => f.name).join("/");
    window.history.replaceState(null, "", `/cms/image-gallery/${newPath}`);
  };





  const handleBreadcrumbClick = (folder) => {
    setOpenedFolder(folder);
    const index = breadcrumb.findIndex((b) => b._id === folder._id);
    if (index !== -1) {
      const newBreadcrumb = breadcrumb.slice(0, index + 1);
      setBreadcrumb(newBreadcrumb);
      const newPath = newBreadcrumb.map(f => f.name).join("/");
      window.history.replaceState(null, "", `/cms/image-gallery/${newPath}`);
    }
  };



  // ==================== Image handleling ======================
  const handleDeleteImage = async (image) => {
    if (!image || !window.confirm(`Are you sure you want to delete ${image.title}?`)) return;

    try {
      await dispatch(deleteImage({ folderID: image.folder, imageID: image._id })).unwrap();
      await dispatch(fetchFolderData(image.folder)).unwrap(); // ✅ Refresh images in real time
      window.alert('Image deleted successfully');
    } catch (error) {
      console.error('Error deleting image:', error);
      window.alert('Failed to delete image.');
    }
  };



  const handleEditImage = async (image) => {
    setIsEditMode(true);
    setImageUpload(true);

    console.log("🖼️ Editing Image - Fetching Latest Data:", image);

    try {
      // ✅ Fetch latest image details from the database
      const response = await dispatch(fetchFolderData(image.folder)).unwrap();

      if (response.images) {
        // ✅ Find the selected image inside the fetched images
        const updatedImage = response.images.find((img) => img._id === image._id);

        if (updatedImage) {
          console.log("✅ Updated Image Data:", updatedImage);

          // ✅ Modify HEIC image path to request JPG instead
          if (updatedImage.fileName.endsWith(".heic")) {
            updatedImage.originalFilePath = updatedImage.originalFilePath.replace(".heic", ".jpg");
          }

          setSelectedImage(updatedImage); // ✅ Store latest image data
        }
        else {
          console.warn("⚠️ Image not found in API response, using previous data.");
          setSelectedImage(image); // ✅ Fallback to existing data
        }
      }
    } catch (error) {
      console.error("❌ Error fetching image data for editing:", error);
      setSelectedImage(image); // ✅ Fallback in case of API failure
    }
  };


  const selectedImagefromCMS = (image) => {
    if (window.opener) {
      window.opener.postMessage({ selectedImage: image }, "*");
    }
    setImageContextMenu(null);
    window.close();
  }


  // =================== Render context =======================
  const renderChildFolders = (folder,) => {
    return folder?.children?.length > 0 ? (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
        {folder.children.map((subfolder, index) => (
          <div
            key={index || subfolder._id}
            className="folder"
            onClick={() => handleFolderClick(subfolder)}
            onContextMenu={(e) => handleRightClickFolder(e, subfolder)}
            onDoubleClick={() => handleDoubleClick(subfolder)}
          >
            <FaFolder />
            <div>{subfolder.name || 'Untitled Folder'}</div>
          </div>
        ))}
      </div>
    ) : (
      <p>No child folders available.</p>
    );
  };


  const ContextMenu = ({ x, y, children }) => (
    <div
      style={{
        position: "absolute",
        top: y,
        left: x,
        backgroundColor: "white",
        border: "1px solid #ddd",
        padding: "10px",
        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
        zIndex: 5000,
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
      className="context-menu"
    >
      {children}
    </div>
  );

  const renderFolderContextMenu = () =>
    contextMenu?.folder && (

      <>

        <ContextMenu x={contextMenu.x} y={contextMenu.y}>
          <button
            className="deleteButton icon-btn"
            onClick={() => handleDeleteFolder(contextMenu.folder._id)}
          >
            <MdFolderDelete /> Delete
          </button>
        </ContextMenu></>
    );

  const renderImageContextMenu = () =>
    imageContextMenu?.image && (
      <ContextMenu x={imageContextMenu.x} y={imageContextMenu.y}>
        <button
          className="editButton icon-btn justify-content-start"
          onClick={() => handleEditImage(imageContextMenu.image)}
        >
          <FaEdit /> Edit
        </button>
        <button
          className="selectImageButton icon-btn"
          onClick={() => selectedImagefromCMS(imageContextMenu.image)}
        >
          Select Image
        </button>
        <button
          className="deleteButton icon-btn justify-content-start"
          onClick={() => handleDeleteImage(imageContextMenu.image)}
        >
          <MdFolderDelete /> Delete
        </button>
      </ContextMenu>
    );

  const renderBreadcrumb = () => (
    <div className="breadcrumb-container">
      {/* Default Home breadcrumb */}
      <span className="breadcrumb-item">
        <span className="breadcrumb-link"
          onClick={() => {
            setOpenedFolder(null); // ✅ Close any opened folder
            setBreadcrumb([]); // ✅ Reset breadcrumb
            localStorage.removeItem("openedFolderId"); // ✅ Clear saved folder
            window.history.replaceState(null, "", "/cms/image-gallery"); // ✅ Reset URL
          }}>
          Home
        </span>

        {breadcrumb.length > 0 && <span className="breadcrumb-separator"> <MdOutlineKeyboardArrowRight style={{ fontSize: "28px" }} /> </span>}
      </span>

      {/* Dynamic breadcrumb */}
      {breadcrumb.map((folder, index) => (
        <span key={folder._id}
          className={`breadcrumb-item ${index === breadcrumb.length - 1 ? "active" : ""}`}
        >
          <span
            className="breadcrumb-link"
            onClick={() => handleBreadcrumbClick(folder)}
          >
            {folder.name || "Untitled Folder"}
          </span>
          {index < breadcrumb.length - 1 && <span className="breadcrumb-separator"> <MdOutlineKeyboardArrowRight style={{ fontSize: "28px" }} /> </span>}
        </span>
      ))}
    </div>
  );


  // =========== Component Renders ===========
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{(openedFolder?.name ? openedFolder.name + " image manager" : "Image Gallery")}</title>
        <link rel="canonical" href="http://mysite.com/example" />
      </Helmet>

      <div style={{ backgroundColor: 'white', padding: '15px', height: '100%', display: 'flex', flexDirection: 'column', rowGap: '20px' }}>

        <div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: "space-between", columnGap: '15px', marginBottom: "15px" }}>
            <div className='row'>
              <input
                type="text"
                value={folderName}
                onChange={handleFolderNameChange}
                placeholder="Enter new folder name"
              />


              <button className="addFolder" onClick={handleAddFolder} disabled={loading}>
                <FaFolderPlus />
                {loading ? 'Adding...' : 'Add Folder'}
              </button>

            </div>

            {/* Upload Images */}
            <div className="row align-items-center" style={{ columnGap: "30px" }}>
              <div onClick={toggleFullScreen}>
                <SlSizeFullscreen style={{ fontSize: "24px", cursor: "pointer" }} />
              </div>

              <button className="addFolder" onClick={() => setImageUpload(true)}>
                <FaImage />
                {loading ? 'Uploading...' : 'Upload Image'}
              </button>
            </div>
          </div>
          {renderBreadcrumb()}
        </div>

        {!openedFolder && folderTree?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: "column", rowGap: '10px' }}>
            <b>All Folders</b>
            {loading ? (
              <p>Loading folders...</p>
            ) : error ? (
              <p style={{ color: 'red' }}>Error loading folders: {error}</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                {folderTree.map((folder) => (
                  <div
                    key={folder._id}
                    className="folder"
                    onClick={() => handleFolderClick(folder)}
                    onContextMenu={(e) => handleRightClickFolder(e, folder)}
                    onDoubleClick={() => handleDoubleClick(folder)}
                  >
                    <FaFolder />
                    <div>{folder.name || 'Untitled Folder'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {openedFolder ? (
          <div style={{ display: 'flex', flexDirection: "column", rowGap: '10px' }}>
            <b>Opened Folder: {openedFolder.name || 'Untitled Folder'}</b>
            <div key={key}>{renderChildFolders(openedFolder)}</div>
          </div>
        ) : null}

        <hr />
        {/* Open folder image */}
        <div>
          {imageDetails.length === 0 ? (
            <p>No images available in this folder.</p>
          ) : (
            <div className={`image-gallery ${isFullScreen ? 'fullscreen' : ''}`}>
              {imageDetails.map((image, index) => (
                <div
                  key={index}
                  onContextMenu={(e) => handleRightClickImage(e, image)}>
                  <img
                    src={
                      image.fileName.endsWith(".heic")
                        ? image.originalFilePath.replace(".heic", ".jpg")
                        : image.compressedFilePath || image.originalFilePath
                    }
                    alt={image.alt || "Uploaded Image"}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}
        </div>




        {/* Image Form */}
        {
          imageUpload && (
            <ImageUploadForm
              setImageUpload={setImageUpload}
              openedFolderID={openedFolderId}
              selectedImage={selectedImage}
              isEditMode={isEditMode}
            />
          )
        }

        {/* Render Context Menu */}
        {renderFolderContextMenu()}
        {renderImageContextMenu()}
      </div >
    </>
  );
};

export default FolderManager;
